import { http } from "./http";

export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("image", file);

  try {
    const response = await http.post("/products/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.imageUrl;
  } catch (err) {
    console.error("Upload error:", err);
    throw err;
  }
};

export type StockType = 'in_store' | 'online';

// A quantity break: unit price when ordering at least `min_qty`. Smallest
// min_qty acts as the MOQ.
export type WholesaleBreak = {
  min_qty: number;
  price: number;
};

export type AdminVariantPayload = {
  sku: string;
  price: number;
  wholesale_price?: number | null; // legacy single price (default tier); null clears
  wholesale_min_qty?: number | null; // legacy MOQ
  wholesale_breaks?: WholesaleBreak[]; // quantity-break schedule (default tier)
  image_url?: string | null; // ✅ allow null = delete
  variant_name?: string;
  attributes: { name: string; value: string }[];
  locations: { name: string; stocks: { stock_type: StockType; stock: number }[] }[];
};

export type AdminProductPayload = {
  name: string;
  sku: string;
  description?: string;
  price: number;
  supplier_price: number;
  wholesale_price?: number | null; // legacy single price (default tier); null clears
  wholesale_min_qty?: number | null; // legacy MOQ
  wholesale_breaks?: WholesaleBreak[]; // quantity-break schedule (default tier)
  availability?: 'both' | 'wholesale_only' | 'retail_only';
  backorder_policy?: 'inherit' | 'none' | 'wholesale_only' | 'both';
  image_url?: string | null; // ✅ allow null = delete
  category_id: number | null;
  option_order?: string[];
  variants: AdminVariantPayload[];
};

export type AdminProductResponse = {
  product_id: number;
  product_name: string;
  sku: string;
  description: string;
  price: number;
  supplier_price: number;
  wholesale_price?: number | null;
  wholesale_min_qty?: number | null;
  wholesale_breaks?: WholesaleBreak[];
  availability?: 'both' | 'wholesale_only' | 'retail_only';
  backorder_policy?: 'inherit' | 'none' | 'wholesale_only' | 'both';
  image_url?: string | null;
  option_order?: string[];
  category: string | null;
  variants: Array<{
    id?: number;
    sku: string;
    price: number;
    wholesale_price?: number | null;
    wholesale_min_qty?: number | null;
    wholesale_breaks?: WholesaleBreak[];
    image_url?: string | null;
    variant_name?: string;
    attributes: { name: string; value: string }[]; // ✅ normalized
    variant_key?: string;
    locations: Array<{ location_name: string; stock_type: StockType; stock: number }>;
  }>;
  option_groups?: Array<{ name: string; values: string[] }>;
  option_value_meta?: OptionValueMetaRow[];
};

export type OptionValueMetaPayload = {
  option_name: string;
  option_value: string;
  description: string | null;
};

export type OptionValueMetaRow = {
  option_name: string;
  option_value: string;
  description: string | null;
};

/**
 * Product Management APIs
 */

// Fetch all products as a bare array (used by OrderForm / SellPage).
// The backend `/products` endpoint now returns a paginated OBJECT, so request a
// high limit and unwrap `products` to preserve the previous array contract.
export const fetchProducts = async () => {
  const response = await http.get("/products", { params: { limit: 100000 } });
  return (response.data.products || []).map((product: any) => ({
    ...product,
    variants: product.variants || [], // Ensure variants is always an array
  }));
};

// Fetch a single server-side page of products (search / filter / sort / paginate).
export const fetchProductsPage = async (opts?: {
  page?: number;
  limit?: number;
  search?: string;
  category_id?: number;
  sort?: string;
}): Promise<{
  products: any[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}> => {
  const response = await http.get("/products", {
    params: {
      page: opts?.page,
      limit: opts?.limit,
      search: opts?.search,
      category_id: opts?.category_id,
      sort: opts?.sort,
    },
  });

  const { products = [], page, limit, total, totalPages } = response.data;

  return {
    products: products.map((product: any) => ({
      ...product,
      variants: product.variants || [], // Ensure variants is always an array
    })),
    page,
    limit,
    total,
    totalPages,
  };
};

// Add a new product
export const addProduct = async (
  productData: AdminProductPayload
): Promise<{ message: string; productId: number }> => {
  const response = await http.post("/products", productData);
  return response.data;
};

export const updateProduct = async (id: number, productData: AdminProductPayload) => {
  const response = await http.put(`/products/${id}`, productData);
  return response.data;
};

// Delete a product
export const deleteProduct = async (id: number) => {
  const response = await http.delete(`/products/${id}`);
  return response.data;
};

// Fetch a single product by ID (with variants and inventory)
export const fetchProductById = async (id: number): Promise<AdminProductResponse> => {
  const response = await http.get(`/products/${id}`);
  const product = response.data as AdminProductResponse;
  product.variants = product.variants || [];
  return product;
};

/**
 * User Management APIs
 */

// Fetch all users
export const fetchUsers = async () => {
  try {
    const response = await http.get("/users");
    return response.data; // Direct array of users
  } catch (err) {
    console.error('Error fetching users:', err);
    throw err;
  }
};

// Add a new user.
// `password` is optional: when omitted, the backend creates the account in a
// pending state and emails the customer a secure link to set their own password.
// The response includes `setupEmailSent` in that case.
export const addUser = async (userData: {
  email: string;
  password?: string;
  role: string;
  name?: string;
  phone?: string;
  location_id?: number | null;
  is_wholesale?: boolean;
  wholesale_tier_id?: number | null;
}): Promise<{ id: number; email: string; setupEmailSent?: boolean; [key: string]: unknown }> => {
  const response = await http.post("/users", userData);
  return response.data;
};

// Update a user
export const updateUser = async (
  id: number,
  userData: {
    email?: string;
    password?: string;
    role?: string;
    name?: string;
    phone?: string;
    location_id?: number | null;
    is_wholesale?: boolean;
    wholesale_tier_id?: number | null;
  }
) => {
  const response = await http.put(`/users/${id}`, userData);
  return response.data;
};

// Delete a user
export const deleteUser = async (id: number) => {
  const response = await http.delete(`/users/${id}`);
  return response.data;
};

// Fetch user by ID
export const fetchUserById = async (id: number) => {
  const response = await http.get(`/users/${id}`);
  return response.data;
};

/**
 * Location Management APIs
 */

// Fetch all locations (for stock management)
export const fetchLocations = async () => {
  const response = await http.get("/locations");
  return response.data; // Ensure this returns `{ locations: [...] }` from the backend
};

// Update a location
export const updateLocation = async (
  id: number,
  locationData: {
    name?: string;
    city?: string;
    province?: string;
    adminEmail?: string;
    adminPassword?: string;
    adminName?: string;
    adminPhone?: string;
  }
) => {
  const response = await http.put(`/locations/${id}`, locationData);
  return response.data;
};

// Delete a location
export const deleteLocation = async (id: number) => {
  const response = await http.delete(`/locations/${id}`);
  return response.data;
};

// Fetch location by ID
export const fetchLocationById = async (id: number) => {
  const response = await http.get(`/locations/${id}`);
  return response.data;
};

// Add a new location
export const addLocation = async (locationData: {
  name: string;
  city: string;
  province: string;
  adminName: string;
  adminEmail: string;
  adminPhone?: string;
  adminPassword: string; // Explicitly required for new locations
}) => {
  const response = await http.post("/locations", locationData);
  return response.data;
};

/**
 * Category Management APIs
 */

// Fetch all categories
export const fetchCategories = async (): Promise<{
  categories: Array<{ id: number; name: string; slug: string }>;
}> => {
  const response = await http.get("/categories");
  return response.data;
};

export const addCategory = async (
  categoryName: string
): Promise<{ id: number; name: string; slug: string }> => {
  const response = await http.post("/categories", { name: categoryName });
  return response.data;
};

export const fetchUserDetails = async (id: number, params: any = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await http.get(
    `/users/${id}/details${query ? `?${query}` : ''}`
  );
  return response.data;
};

// Admin-managed customer addresses (Super Admin acting on a customer's addresses)
export interface AdminAddressPayload {
  label?: string;
  full_name?: string;
  phone?: string;
  email?: string;
  address: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  is_default?: boolean;
}

export const adminAddUserAddress = async (userId: number, payload: AdminAddressPayload) => {
  const res = await http.post(`/users/${userId}/addresses`, payload);
  return res.data;
};

export const adminUpdateUserAddress = async (userId: number, addressId: number, payload: Partial<AdminAddressPayload>) => {
  const res = await http.put(`/users/${userId}/addresses/${addressId}`, payload);
  return res.data;
};

export const adminDeleteUserAddress = async (userId: number, addressId: number) => {
  const res = await http.delete(`/users/${userId}/addresses/${addressId}`);
  return res.data;
};

// Upsert option-value meta (variant descriptions by option/value)
export const upsertProductOptionValueMeta = async (
  productId: number,
  payload: OptionValueMetaPayload
) => {
  const response = await http.post(`/products/${productId}/option-meta`, payload);
  return response.data;
};


export const fetchMedia = async () => {
  const res = await http.get('/media');
  return res.data.media;
};

export const uploadMedia = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await http.post('/media/upload', formData);
  return res.data;
};

export const deleteMedia = async (id: number) => {
  await http.delete(`/media/${id}`);
};

export const updateMedia = async (
  id: number,
  payload: {
    title?: string | null;
    alt_text?: string | null;
    tags?: string[];
  }
) => {
  const res = await http.patch(`/media/${id}`, payload);
  return res.data.media;
};